import pb, { requireAuth } from '../pocketbaseClient';
import { generateAccessToken } from '../utils/accessToken';

/**
 * Fragebogen 2: the offline questionnaire a candidate receives after passing
 * the first two rounds, plus the invitation mail that carries its link.
 *
 * Lives in PocketBase rather than the CMS because the invitation tokens and the
 * send status live here too, and a candidate opening a link should not depend
 * on a call between two servers.
 *
 * The document is keyed by the CMS job id. That is unambiguous, unlike the file
 * name, which the CMS rewrites on upload collisions. A job deleted and
 * re-imported in the CMS gets a new id, and its questionnaire has to be
 * attached again.
 */

const COLLECTION = 'assessments';

/** With getList/getFullList, a 404 from the server really does mean the
 * collection is absent: an empty result is a 200 with an empty page. */
const NOT_FOUND = 404;

const MISSING_COLLECTION_HINT =
  `Die PocketBase-Collection "${COLLECTION}" existiert nicht. ` +
  'Bitte im PocketBase-Admin anlegen.';

/**
 * Field-level reasons PocketBase returns for a validation failure.
 *
 * PocketBase's JS SDK (0.26) surfaces the raw response body via the `data`
 * getter on `ClientResponseError`, which is just an alias for `.response` —
 * so `error.data.data` is the server's `{ field: { code, message } }` map,
 * the same object `error.response.data` would give. Without this, a 400
 * collapses to the generic "Failed to create record." on both sides of a
 * debugging session, hiding which field the server actually rejected.
 */
function fieldErrors(error) {
    const fields = error?.data?.data;
    if (!fields || typeof fields !== 'object') return '';
    const named = Object.entries(fields)
        .map(([field, detail]) => `${field}: ${detail?.message || detail?.code || 'ungültig'}`)
        .join(', ');
    return named ? ` (${named})` : '';
}

function describeError(error, fallback) {
    if (error?.status === NOT_FOUND) return MISSING_COLLECTION_HINT;
    const base = error?.message ? `${fallback}: ${error.message}` : fallback;
    return `${base}${fieldErrors(error)}`;
}

function normalize(record) {
    if (!record) return null;
    return {
        id: record.id,
        // Kept as PocketBase reports them: pb.files.getURL reads the collection
        // off the record rather than taking it as an argument.
        collectionId: record.collectionId,
        collectionName: record.collectionName,
        cmsJobId: record.cms_job_id,
        label: record.label || '',
        filename: record.document || null,
        emailSubject: record.email_subject || '',
        emailBody: record.email_body || '',
        updatedAt: record.updated || null,
    };
}

class AssessmentService {
    /** The questionnaire attached to one CMS job, or null. */
    async getByJobId(jobId) {
        try {
            requireAuth();
            // getList, not getFirstListItem: the latter invents a 404 client-side
            // when nothing matches, and that fake error carries no url, so it is
            // indistinguishable from the collection being absent. An empty page
            // is simply an empty page.
            const page = await pb
                .collection(COLLECTION)
                .getList(1, 1, { filter: pb.filter('cms_job_id = {:id}', { id: String(jobId) }) });
            return { success: true, data: normalize(page.items[0]) };
        } catch (error) {
            return { success: false, error: describeError(error, 'Fragebogen konnte nicht geladen werden') };
        }
    }

    /** Every questionnaire in full, for the download of all jobs. */
    async getAll() {
        try {
            requireAuth();
            const records = await pb.collection(COLLECTION).getFullList();
            return { success: true, data: records.map(normalize) };
        } catch (error) {
            return { success: false, error: describeError(error, 'Fragebögen konnten nicht geladen werden'), data: [] };
        }
    }

    /** Every job id that already has a questionnaire, for the card indicator. */
    async getJobIdsWithAssessment() {
        try {
            requireAuth();
            const records = await pb
                .collection(COLLECTION)
                .getFullList({ fields: 'cms_job_id,document' });
            return {
                success: true,
                data: new Set(records.filter((r) => r.document).map((r) => r.cms_job_id)),
            };
        } catch (error) {
            // The list is only used to show a hint, so a missing collection
            // must not take the whole page down with it.
            return { success: false, error: describeError(error, 'Fragebögen konnten nicht geladen werden'), data: new Set() };
        }
    }

    /**
     * Create or replace the questionnaire for a job.
     * `htmlFile` is optional on an update: the mail text can change on its own.
     */
    async save({ jobId, label, htmlFile, emailSubject, emailBody, existingId, removeDocument }) {
        try {
            requireAuth();

            const formData = new FormData();
            formData.append('cms_job_id', String(jobId));
            formData.append('label', label || '');
            formData.append('email_subject', emailSubject || '');
            formData.append('email_body', emailBody || '');
            if (htmlFile) {
                formData.append('document', htmlFile, htmlFile.name);
            } else if (removeDocument) {
                // An empty value is how PocketBase deletes a single file. The
                // field holds one document, so a new upload replaces the old
                // one; nothing accumulates.
                formData.append('document', '');
            }

            const record = existingId
                ? await pb.collection(COLLECTION).update(existingId, formData)
                : await pb.collection(COLLECTION).create(formData);

            return { success: true, data: normalize(record) };
        } catch (error) {
            return { success: false, error: describeError(error, 'Speichern fehlgeschlagen') };
        }
    }

    async remove(recordId) {
        try {
            requireAuth();
            await pb.collection(COLLECTION).delete(recordId);
            return { success: true };
        } catch (error) {
            return { success: false, error: describeError(error, 'Löschen fehlgeschlagen') };
        }
    }

    /**
     * A short-lived URL for HR to read the questionnaire themselves.
     *
     * The file field is protected, so the plain file URL is rejected without a
     * token. This one is tied to the logged-in HR user and expires; it is not
     * the link a candidate receives.
     */
    async getPreviewUrl(record) {
        if (!record?.id || !record?.filename) return { success: false, error: 'Kein Dokument hinterlegt.' };
        try {
            requireAuth();
            const token = await pb.files.getToken();
            const url = pb.files.getURL(record, record.filename, { token });
            return { success: true, data: url };
        } catch (error) {
            return { success: false, error: describeError(error, 'Vorschau nicht möglich') };
        }
    }
}

/** Where a candidate opens the questionnaire. Same host and shape as Fragebogen 1. */
const APPLICANT_BASE =
  process.env.REACT_APP_APPLICANT_BASE_URL || 'https://fragebogen.karrieresichern.de';

export function buildAssessmentLink(assessmentId, token) {
  return `${APPLICANT_BASE}?page=fragebogen2&id=${encodeURIComponent(assessmentId)}&t=${encodeURIComponent(token)}`;
}

const INVITES = 'assessment_invites';

/**
 * Carries the candidate's token to PocketBase. The collection's list rule only
 * lets a guest see the record whose token matches this header, so a guest
 * without it sees nothing, and one with it sees exactly their own invitation.
 */
function inviteTokenHeaders(token) {
    return { 'X-Invite-Token': token };
}

/** The fields the candidate's page writes, shared by the full invite and the card summary. */
function activityFields(record) {
    return {
        openedAt: record.opened_at || null,
        progress: typeof record.progress === 'number' ? record.progress : 0,
        lastActiveAt: record.last_active_at || null,
        submittedAt: record.submitted_at || null,
    };
}

function normalizeInvite(record) {
    if (!record) return null;
    return {
        ...activityFields(record),
        id: record.id,
        token: record.token,
        assessmentId: record.assessment,
        email: record.email || '',
        status: record.status || 'pending',
        sentAt: record.sent_at || null,
        documentUrl: record.document_url || null,
        link: buildAssessmentLink(record.assessment, record.token),
        createdAt: record.created || null,
        // Only present when read together with `applicant`; listForApplicant
        // uses it to narrow the broad server-side match down exactly.
        applicantId: record.applicant || null,
        // For {Vorname} when the mail for this invitation is opened again,
        // days or weeks after it was created — nothing else remembers it.
        firstName: record.first_name || '',
        // What the candidate's page read out of the questionnaire when they
        // sent it: one entry per question, plus the document's own verdict.
        // Empty for every invitation sent before this existed.
        answers: Array.isArray(record.answers) ? record.answers : [],
        result: record.result && typeof record.result === 'object' ? record.result : null,
        // Only populated when read with expand. The status card needs it to
        // say which job this invitation belongs to.
        cmsJobId: record.expand?.assessment?.cms_job_id || null,
    };
}

/**
 * Fields added by hand in the PocketBase admin. An invitation is worth more
 * than any of them, so a create that trips over one is retried without it —
 * but only over the fields the server actually named, so a real validation
 * error still surfaces.
 */
const OPTIONAL_FIELDS = ['applicant', 'first_name'];

/** Written only where the invite collection already has the fields for them. */
const ANSWER_FIELDS = ['answers', 'result'];

/**
 * Creates the invite record, retrying once without whichever OPTIONAL_FIELDS
 * the server actually blamed for the failure.
 *
 * Checked by value, not by key: `applicant` is always sent (as `null` when
 * there is no applicantId), so key presence alone can't tell "nothing to
 * retry without" from "something PocketBase rejected". A blind retry on an
 * unrelated failure would mask a real validation error, so an empty
 * intersection rethrows unchanged, and this retries at most once.
 */
async function createInviteRecord(data) {
    try {
        return await pb.collection(INVITES).create(data);
    } catch (createError) {
        const blamed = createError?.data?.data || {};
        const dropped = OPTIONAL_FIELDS.filter((field) => data[field] && field in blamed);
        if (dropped.length === 0) throw createError;
        console.warn(
            `Invite create rejected ${dropped.join(', ')}, retrying without ${dropped.length === 1 ? 'it' : 'them'}:`,
            createError
        );
        const retryData = { ...data };
        for (const field of dropped) delete retryData[field];
        return await pb.collection(INVITES).create(retryData);
    }
}

class InviteService {
    /**
     * One invitation, one token, one candidate.
     *
     * The file URL is copied onto the invite so an unauthenticated candidate
     * only ever reads this one record: `assessments` itself stays closed.
     */
    async create({ assessment, email, applicantId, firstName, token: presetToken }) {
        try {
            requireAuth();
            // The dialog generates the token up front so it can show the
            // finished mail with its real link before anything is saved.
            // Whatever the preview showed is then what actually goes out.
            const token = presetToken || generateAccessToken();
            const documentUrl = assessment?.filename
                ? pb.files.getURL(assessment, assessment.filename)
                : '';

            const data = {
                token,
                assessment: assessment.id,
                applicant: applicantId || null,
                email,
                status: 'pending',
                document_url: documentUrl,
            };
            // Omitted rather than sent empty: an old invitation with no name
            // on it is expected, a stored empty string would look like a bug.
            const trimmedFirstName = String(firstName || '').trim();
            if (trimmedFirstName) data.first_name = trimmedFirstName;

            const record = await createInviteRecord(data);
            return { success: true, data: normalizeInvite(record) };
        } catch (error) {
            return { success: false, error: describeError(error, 'Einladung konnte nicht erstellt werden') };
        }
    }

    /**
     * Marks that HR opened the mail for this invitation.
     *
     * "Sent" means the mail programme was opened with the draft — the browser
     * cannot see whether it was actually sent, the same limitation the
     * candidate-side "submitted" flag has.
     */
    async markSent(inviteId) {
        try {
            requireAuth();
            const record = await pb.collection(INVITES).update(inviteId, {
                sent_at: new Date().toISOString(),
                status: 'sent',
            });
            return { success: true, data: normalizeInvite(record) };
        } catch (error) {
            return { success: false, error: describeError(error, 'Der Versand konnte nicht vermerkt werden') };
        }
    }

    async listForAssessment(assessmentId) {
        try {
            requireAuth();
            const records = await pb.collection(INVITES).getFullList({
                filter: pb.filter('assessment = {:id}', { id: assessmentId }),
                sort: '-created',
            });
            return { success: true, data: records.map(normalizeInvite) };
        } catch (error) {
            return { success: false, error: describeError(error, 'Einladungen konnten nicht geladen werden'), data: [] };
        }
    }

    /**
     * The invitations that belong to one application, for the status card.
     *
     * `applicant` is the exact path, the mail address the fallback: the column
     * has always been written but never read, and whether PocketBase even
     * knows it is unverified. An unknown field in the filter earns a 400 from
     * the server — then it asks by address alone. Older applications never had
     * `applicant` set to begin with.
     *
     * Both filters search with `~` (contains) rather than `=` (exact), because
     * `=` compares case-sensitively: an invitation typed by hand in the old
     * Stellenanzeigen dialog may read "Anna@Example.de" while this feature
     * always derives the address in lower case, and it is exactly those older,
     * hand-typed invitations that also carry no `applicant` value — with `=`
     * they would be invisible on both counts. `~` finds them regardless of
     * case, but it is a contains search, so asking for "anna@example.de" also
     * drags in "hanna@example.de". The exact, case-folded comparison below is
     * what separates a real match from a near miss the broad search pulled in.
     *
     * That comparison also decides whether the address is allowed to match at
     * all. See the comment on `belongsHere` below.
     */
    async listForApplicant({ submissionId, email }) {
        const mail = String(email || '').trim();
        const byEmail = mail ? pb.filter('email ~ {:mail}', { mail }) : null;
        const byBoth =
            submissionId && mail
                ? pb.filter('applicant = {:id} || email ~ {:mail}', { id: submissionId, mail })
                : submissionId
                  ? pb.filter('applicant = {:id}', { id: submissionId })
                  : byEmail;

        if (!byBoth) return { success: true, data: [] };

        const wanted = mail.toLowerCase();
        // An invitation belongs to this application when its `applicantId`
        // says so directly. The mail-address fallback is restricted to
        // invitations that carry no `applicantId` at all — legacy records
        // from the older Stellenanzeigen dialog, which never set one, and
        // for which the address is the best evidence available. An invitation
        // that names a *different* applicant must never match by address:
        // otherwise a returning candidate's invitation from an earlier
        // application would leak onto a new one and read as already sent.
        const belongsHere = (invite) =>
            (submissionId && invite.applicantId === submissionId) ||
            (!invite.applicantId && wanted && String(invite.email || '').trim().toLowerCase() === wanted);

        const read = async (filter) => {
            const records = await pb.collection(INVITES).getFullList({
                filter,
                sort: '-created',
                expand: 'assessment',
            });
            return records.map(normalizeInvite).filter(belongsHere);
        };

        try {
            requireAuth();
            return { success: true, data: await read(byBoth) };
        } catch (error) {
            if (error?.status === 400 && byEmail && byBoth !== byEmail) {
                try {
                    return { success: true, data: await read(byEmail) };
                } catch (retryError) {
                    return {
                        success: false,
                        error: describeError(retryError, 'Einladungen konnten nicht geladen werden'),
                        data: [],
                    };
                }
            }
            return {
                success: false,
                error: describeError(error, 'Einladungen konnten nicht geladen werden'),
                data: [],
            };
        }
    }

    /** Which applications already carry an invitation, for the list filter. */
    async listInvitedKeys() {
        try {
            requireAuth();
            const records = await pb.collection(INVITES).getFullList({ fields: 'email,applicant' });
            const applicantIds = new Set();
            const emailsWithoutApplicant = new Set();
            for (const record of records) {
                if (record.applicant) {
                    applicantIds.add(String(record.applicant));
                } else {
                    const mail = String(record.email || '').trim().toLowerCase();
                    if (mail) emailsWithoutApplicant.add(mail);
                }
            }
            return { success: true, data: { applicantIds, emailsWithoutApplicant } };
        } catch (error) {
            return {
                success: false,
                error: describeError(error, 'Einladungen konnten nicht geladen werden'),
                data: { applicantIds: new Set(), emailsWithoutApplicant: new Set() },
            };
        }
    }

    /**
     * Read one invitation by its token, without being logged in.
     *
     * This is the candidate's path. The list rule must admit guests only via
     * the X-Invite-Token header (see docs/fragebogen-2-einladungen.md): knowing
     * a token gets you that one record, and nothing lets you enumerate the rest.
     */
    async getByToken(token) {
        if (!token) return { success: false, error: 'Kein Zugangscode im Link.' };
        try {
            const page = await pb
                .collection(INVITES)
                .getList(1, 1, {
                    filter: pb.filter('token = {:t}', { t: token }),
                    headers: inviteTokenHeaders(token),
                });
            const invite = normalizeInvite(page.items[0]);
            if (!invite) return { success: false, error: 'Dieser Link ist ungültig.' };
            return { success: true, data: invite };
        } catch (error) {
            return { success: false, error: 'Dieser Link konnte nicht geprüft werden.' };
        }
    }

    /** Revokes an invitation. Its link stops working immediately. */
    async remove(inviteId) {
        try {
            requireAuth();
            await pb.collection(INVITES).delete(inviteId);
            return { success: true };
        } catch (error) {
            return { success: false, error: describeError(error, 'Einladung konnte nicht gelöscht werden') };
        }
    }

    /**
     * Writes the candidate's tracking fields (opened, progress, last activity,
     * sent). Failure never reaches the candidate: it only costs HR the status.
     *
     * Reports whether the write went out as asked — true only for the full
     * payload. The submit asks, because it may only tell the candidate "ist
     * angekommen" once the answers themselves are stored; everything else
     * ignores the answer and keeps going.
     *
     * requestKey: null because the SDK otherwise cancels an update still in
     * flight when the next one for the same record starts. keepalive lets the
     * last write leave a page that is closing.
     */
    async track(invite, fields, { keepalive = false } = {}) {
        if (!invite?.id) return false;
        const write = (payload) => pb.collection(INVITES).update(invite.id, payload, {
            headers: inviteTokenHeaders(invite.token),
            requestKey: null,
            ...(keepalive ? { fetch: (url, config) => fetch(url, { ...config, keepalive: true }) } : {}),
        });

        try {
            await write(fields);
            return true;
        } catch {
            // A PocketBase that has not had scripts/pocketbase-invite-tracking.mjs
            // applied yet rejects the whole update over answers/result, which
            // would take "abgeschickt" down with them. The status matters more
            // than the answers, so it goes out on its own instead.
            const fallback = Object.fromEntries(
                Object.entries(fields).filter(([field]) => !ANSWER_FIELDS.includes(field))
            );
            if (Object.keys(fallback).length === Object.keys(fields).length) return false;
            try {
                await write(fallback);
            } catch {
                // See above: the questionnaire keeps working either way.
            }
            return false;
        }
    }

    /** Every invitation's tracking fields, grouped by CMS job id, for the card line. */
    async listActivityByJob() {
        try {
            requireAuth();
            const records = await pb.collection(INVITES).getFullList({
                expand: 'assessment',
                fields: 'opened_at,progress,last_active_at,submitted_at,expand.assessment.cms_job_id',
            });
            const byJob = new Map();
            for (const record of records) {
                const jobId = record.expand?.assessment?.cms_job_id;
                if (!jobId) continue;
                const key = String(jobId);
                byJob.set(key, [...(byJob.get(key) || []), activityFields(record)]);
            }
            return { success: true, data: byJob };
        } catch (error) {
            return { success: false, error: describeError(error, 'Einladungen konnten nicht geladen werden'), data: new Map() };
        }
    }
}

export const assessmentService = new AssessmentService();
export const inviteService = new InviteService();
export default assessmentService;
